import React, { useState, useMemo, useEffect, useRef } from 'react';

import BreakdownTable from '../components/BreakdownTable';
import EstimateDocumentTemplate from '../components/EstimateDocumentTemplate';
import { getCompanyInfo, getEstimateTerms } from '../../api/config';

const EstimateResult = ({ result, isSubmitted = false, onRestart, onReselect, onSubmitInquiry }) => {
    // Initialize isUnlocked from isSubmitted prop (persisted in DB)
    const [isUnlocked, setIsUnlocked] = useState(isSubmitted);
    const [loading, setLoading] = useState(false);

    // Form State
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [contactPerson, setContactPerson] = useState('');
    const [inquiry, setInquiry] = useState('');
    const [wantConsulting, setWantConsulting] = useState(true);
    const [privacyAgreed, setPrivacyAgreed] = useState(false);
    const [showPrivacy, setShowPrivacy] = useState(false);
    const [showResendModal, setShowResendModal] = useState(false);

    // Config State (from API)
    const [supplierInfo, setSupplierInfo] = useState(null);
    const [estimateTerms, setEstimateTerms] = useState(null);

    // Document ref for printing
    const documentRef = useRef(null);

    // Simulation State - editable quantities
    const originalBreakdown = result?.breakdown || [];
    const [simulatedQuantities, setSimulatedQuantities] = useState({});

    const isSimulating = Object.keys(simulatedQuantities).length > 0;

    // Load config on mount
    useEffect(() => {
        const loadConfig = async () => {
            try {
                const [company, terms] = await Promise.all([
                    getCompanyInfo(),
                    getEstimateTerms()
                ]);
                setSupplierInfo(company);
                setEstimateTerms(terms);
            } catch (err) {
                console.error('Failed to load config:', err);
            }
        };
        loadConfig();
    }, []);

    // Calculate current totals based on simulation
    const { breakdown, totalExclVAT, totalInclVAT } = useMemo(() => {
        const bd = originalBreakdown.map((item, idx) => {
            const simQty = simulatedQuantities[idx];
            const qty = simQty !== undefined ? simQty : item.quantity;
            return {
                ...item,
                quantity: qty,
                subtotal: item.unitPrice * qty
            };
        });
        const total = bd.reduce((sum, item) => sum + item.subtotal, 0);
        return {
            breakdown: bd,
            totalExclVAT: total,
            totalInclVAT: Math.round(total * 1.1)
        };
    }, [originalBreakdown, simulatedQuantities]);

    // Original totals (for document - not simulated)
    const originalTotals = useMemo(() => {
        const total = originalBreakdown.reduce((sum, item) => sum + item.subtotal, 0);
        return {
            totalExclVAT: total,
            totalInclVAT: Math.round(total * 1.1)
        };
    }, [originalBreakdown]);

    const handleQuantityChange = (idx, value) => {
        const qty = parseInt(value, 10);
        if (isNaN(qty) || qty < 0) return;
        setSimulatedQuantities(prev => ({ ...prev, [idx]: qty }));
    };

    const resetSimulation = () => {
        setSimulatedQuantities({});
    };

    const handlePrint = () => {
        const oldTitle = document.title;
        document.title = '가까운솔루션-예상견적서';
        window.print();
        // Restore title after print dialog closes (optional, but good practice)
        // Note: window.print() is blocking in some browsers but not others.
        // It's safe to leave the title or restore it after a short delay/event if needed.
        // For simplicity, we can leave it or restore immediately.
        setTimeout(() => {
            document.title = oldTitle;
        }, 1000);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!email) return alert("이메일을 입력해주세요.");
        if (!email.includes('@')) return alert("올바른 이메일 형식이 아닙니다.");
        if (!privacyAgreed) return alert("개인정보 수집 및 이용에 동의해주세요.");

        setLoading(true);
        try {
            const success = await onSubmitInquiry({
                email,
                phone,
                companyName,
                contactPerson,
                message: inquiry,
                wantConsulting
            });

            if (success) {
                alert(`상세 견적서가 ${email}로 발송되었습니다.\n확인해 주세요.`);
                setIsUnlocked(true);
                setShowResendModal(false); // Close modal if resending
            } else {
                alert("메일 발송에 실패했습니다. 이메일 주소를 확인해주세요.");
            }
        } catch (err) {
            console.error(err);
            alert("처리 중 오류가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    // Generate document number
    const documentNumber = useMemo(() => {
        const now = new Date();
        const dateStr = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
        const randomSuffix = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
        return `EST-${dateStr}-${randomSuffix}`;
    }, []);

    return (
        <>
            {/* Screen View */}
            <div className="ae-view ae-result screen-only">
                <h2 className="ae-title">예상 견적 결과</h2>

                {/* Total Price Section */}
                <div className="result-card">
                    <div className="price-box">
                        <span className="label">총 예상 비용 (VAT 별도)</span>
                        <div className="amount-row">
                            <span className="amount">{totalExclVAT.toLocaleString()}원</span>
                            {isSimulating && (
                                <span style={{ marginLeft: '10px', color: '#e65100', fontSize: '0.9rem' }}>
                                    (시뮬레이션)
                                </span>
                            )}
                        </div>
                        <div style={{ marginTop: '5px', fontSize: '0.9rem', color: '#666' }}>
                            VAT 포함: <strong>{totalInclVAT.toLocaleString()}원</strong>
                        </div>
                    </div>
                </div>

                {/* Header with Buttons */}
                <div style={{ marginTop: '30px', marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                    <h3 className="sub-title" style={{ margin: 0 }}>상세 견적 내역</h3>

                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {isSimulating && isUnlocked && (
                            <button
                                onClick={resetSimulation}
                                style={{
                                    padding: '8px 12px',
                                    fontSize: '0.85rem',
                                    background: '#fff',
                                    border: '1px solid #ccc',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginRight: '10px'
                                }}
                            >
                                🔄 초기화
                            </button>
                        )}

                        <button className="ae-btn secondary" onClick={onReselect} style={{ padding: '8px 14px', fontSize: '0.85rem' }}>
                            다른 분야 선택
                        </button>
                        <button className="ae-btn secondary" onClick={onRestart} style={{ padding: '8px 14px', fontSize: '0.85rem' }}>
                            처음부터 다시하기
                        </button>
                        {isUnlocked && (
                            <>
                                <button
                                    className="ae-btn primary"
                                    onClick={() => setShowResendModal(true)}
                                    style={{ padding: '8px 14px', fontSize: '0.85rem', backgroundColor: 'var(--primary-color)', borderColor: 'var(--primary-color)', color: '#fff' }}
                                >
                                    메일 다시 보내기
                                </button>
                                <button className="ae-btn primary" onClick={handlePrint} style={{ padding: '8px 14px', fontSize: '0.85rem', backgroundColor: 'var(--primary-color)', borderColor: 'var(--primary-color)', color: '#fff' }}>
                                    견적서 출력
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Simulation Notice */}
                {isUnlocked && (
                    <div style={{
                        background: '#fff3e0',
                        border: '1px solid #ffcc80',
                        borderRadius: '6px',
                        padding: '10px 15px',
                        marginBottom: '15px',
                        fontSize: '0.85rem',
                        color: '#e65100'
                    }}>
                        ⓘ 수량을 변경하여 예상 금액을 시뮬레이션해 보실 수 있습니다.<br />
                        <span style={{ color: '#888' }}>
                            단, 시뮬레이션 수량은 저장되지 않으며, 상담 요청 시 <strong>최초 산출된 견적 기준</strong>으로 전달됩니다.
                        </span>
                    </div>
                )}

                {/* Breakdown Section (Table + Gate) */}
                <div className="breakdown-section" style={{ position: 'relative' }}>
                    <div className={`ae-breakdown-content ${!isUnlocked ? 'blurred-content' : ''}`} style={{
                        filter: !isUnlocked ? 'blur(6px)' : 'none',
                        pointerEvents: !isUnlocked ? 'none' : 'auto',
                        userSelect: !isUnlocked ? 'none' : 'auto',
                        minHeight: '300px'
                    }}>
                        <BreakdownTable
                            breakdown={breakdown}
                            totals={{ totalExclVAT, totalInclVAT }}
                            mode="screen"
                            simulatedQuantities={simulatedQuantities}
                            onQuantityChange={handleQuantityChange}
                            isUnlocked={isUnlocked}
                        />
                    </div>

                    {/* Gated Overlay Form */}
                    {!isUnlocked && (
                        <div className="gate-overlay" style={{
                            position: 'absolute',
                            top: 0, left: 0, right: 0, bottom: 0,
                            background: 'rgba(255,255,255,0.6)',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 10
                        }}>
                            <div className="gate-form-card" style={{
                                background: '#fff',
                                padding: '30px',
                                borderRadius: '12px',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                                maxWidth: '400px',
                                width: '90%',
                                textAlign: 'center'
                            }}>
                                <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem', color: '#1a237e' }}>
                                    예상 상세 견적서(수량, 단가포함) 받기
                                </h3>
                                <p style={{ margin: '0 0 20px 0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
                                    이메일 주소를 입력하시면 <strong style={{ color: '#e65100' }}>수량과 단가</strong>가 포함된<br />
                                    예상 상세 견적서가 이메일로 <strong style={{ color: '#1a237e' }}>자동 발송</strong>됩니다.
                                </p>

                                <form onSubmit={e => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                    <input
                                        type="text"
                                        placeholder="회사/기관명 (선택)"
                                        className="form-control"
                                        value={companyName}
                                        onChange={e => setCompanyName(e.target.value)}
                                        style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%' }}
                                    />
                                    <input
                                        type="text"
                                        placeholder="담당자명 (선택)"
                                        className="form-control"
                                        value={contactPerson}
                                        onChange={e => setContactPerson(e.target.value)}
                                        style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%' }}
                                    />
                                    <input
                                        type="email"
                                        placeholder="이메일 주소 (필수)"
                                        className="form-control"
                                        required
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%' }}
                                    />
                                    <input
                                        type="tel"
                                        placeholder="연락처 (선택)"
                                        className="form-control"
                                        value={phone}
                                        onChange={e => setPhone(e.target.value)}
                                        style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%' }}
                                    />
                                    <textarea
                                        placeholder="추가 문의사항 (선택)"
                                        className="form-control"
                                        rows={2}
                                        value={inquiry}
                                        onChange={e => setInquiry(e.target.value)}
                                        style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%', resize: 'none' }}
                                    />

                                    <div className="opt-in-check" style={{ textAlign: 'left', fontSize: '0.85rem', color: '#555', marginTop: '5px' }}>
                                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                                            <input
                                                type="checkbox"
                                                checked={wantConsulting}
                                                onChange={e => setWantConsulting(e.target.checked)}
                                                style={{ marginTop: '3px' }}
                                            />
                                            <span>전문가의 상세 안내를 받겠습니다.<br />
                                                <small style={{ color: '#888' }}>(체크를 해제해도 예상 상세 견적서는 자동발송 됩니다)</small></span>
                                        </label>
                                    </div>

                                    {/* Privacy Agreement */}
                                    <div className="terms-agreement" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '6px', textAlign: 'left' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                            <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.85rem' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={privacyAgreed}
                                                    onChange={(e) => setPrivacyAgreed(e.target.checked)}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                <span>개인정보 수집 및 이용 동의 <strong style={{ color: '#e65100' }}>[필수]</strong></span>
                                            </label>
                                            <button
                                                type="button"
                                                onClick={() => setShowPrivacy(!showPrivacy)}
                                                style={{
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#666',
                                                    textDecoration: 'underline',
                                                    fontSize: '0.75rem',
                                                    cursor: 'pointer',
                                                    padding: '0'
                                                }}
                                            >
                                                {showPrivacy ? '접기' : '상세보기'}
                                            </button>
                                        </div>

                                        {/* Inline Privacy Details */}
                                        {showPrivacy && (
                                            <div style={{
                                                marginTop: '8px',
                                                padding: '8px',
                                                backgroundColor: '#fff',
                                                border: '1px solid #eee',
                                                borderRadius: '4px',
                                                fontSize: '0.75rem',
                                                color: '#555',
                                                lineHeight: '1.4'
                                            }}>
                                                <p style={{ margin: '0 0 3px 0' }}><strong>1. 수집항목:</strong> 이메일, 연락처, 이름, 회사명</p>
                                                <p style={{ margin: '0 0 3px 0' }}><strong>2. 수집목적:</strong> 예상 견적서 발송 및 상담</p>
                                                <p style={{ margin: '0 0 0 0' }}><strong>3. 보유기간:</strong> 3년 (상담 이력 관리)</p>
                                            </div>
                                        )}
                                    </div>

                                    <button type="button" onClick={handleSubmit} className="ae-btn primary" disabled={loading} style={{ marginTop: '10px', width: '100%' }}>
                                        {loading ? '발송 중...' : '상세 견적서 받기'}
                                    </button>
                                </form>
                            </div>
                        </div>
                    )}
                </div>

                <div className="actions" style={{ marginTop: '30px' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                        <button className="ae-btn secondary" onClick={onReselect}>
                            다른 분야 선택
                        </button>
                        <button className="ae-btn secondary" onClick={onRestart}>
                            처음부터 다시하기
                        </button>
                        {isUnlocked && (
                            <>
                                <button className="ae-btn primary" onClick={() => setShowResendModal(true)} style={{ backgroundColor: 'var(--primary-color)', borderColor: 'var(--primary-color)', color: '#fff' }}>
                                    메일 다시 보내기
                                </button>
                                <button className="ae-btn primary" onClick={handlePrint} style={{ backgroundColor: 'var(--primary-color)', borderColor: 'var(--primary-color)', color: '#fff' }}>
                                    견적서 출력
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </div>

            {/* Print View - Hidden on screen, shown when printing */}
            <div className="print-only" ref={documentRef}>
                {supplierInfo && estimateTerms && (
                    <EstimateDocumentTemplate
                        breakdown={originalBreakdown}
                        totals={originalTotals}
                        supplier={supplierInfo}
                        recipient={{
                            companyName,
                            contactPerson,
                            email,
                            phone
                        }}
                        terms={estimateTerms}
                        documentNumber={documentNumber}
                        issueDate={new Date()}
                    />
                )}
            </div>

            {/* Resend Email Modal */}
            {showResendModal && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        background: '#fff',
                        padding: '30px',
                        borderRadius: '12px',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        maxWidth: '400px',
                        width: '90%',
                        textAlign: 'center',
                        maxHeight: '90vh',
                        overflowY: 'auto'
                    }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                            <h3 style={{ margin: 0, fontSize: '1.2rem', color: '#1a237e', textAlign: 'left', flex: 1 }}>
                                견적서 다시 보내기
                            </h3>
                            <button
                                onClick={() => setShowResendModal(false)}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: '#666',
                                    padding: '0 0 0 10px',
                                    lineHeight: 1
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <p style={{ margin: '0 0 20px 0', fontSize: '0.9rem', color: '#666', lineHeight: '1.5' }}>
                            정보를 수정하여 견적서를 다시 발송하실 수 있습니다.
                        </p>
                        <form onSubmit={e => e.preventDefault()} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <input
                                type="text"
                                placeholder="회사/기관명 (선택)"
                                value={companyName}
                                onChange={e => setCompanyName(e.target.value)}
                                style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%' }}
                            />
                            <input
                                type="text"
                                placeholder="담당자명 (선택)"
                                value={contactPerson}
                                onChange={e => setContactPerson(e.target.value)}
                                style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%' }}
                            />
                            <input
                                type="email"
                                placeholder="이메일 주소 (필수)"
                                required
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%' }}
                            />
                            <input
                                type="tel"
                                placeholder="연락처 (선택)"
                                value={phone}
                                onChange={e => setPhone(e.target.value)}
                                style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%' }}
                            />
                            <textarea
                                placeholder="추가 문의사항 (선택)"
                                className="form-control"
                                rows={2}
                                value={inquiry}
                                onChange={e => setInquiry(e.target.value)}
                                style={{ padding: '12px', borderRadius: '6px', border: '1px solid #ddd', width: '100%', resize: 'none' }}
                            />

                            <div className="opt-in-check" style={{ textAlign: 'left', fontSize: '0.85rem', color: '#555', marginTop: '5px' }}>
                                <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        checked={wantConsulting}
                                        onChange={e => setWantConsulting(e.target.checked)}
                                        style={{ marginTop: '3px' }}
                                    />
                                    <span>전문가의 상세 안내를 받겠습니다.<br />
                                        <small style={{ color: '#888' }}>(체크를 해제해도 예상 상세 견적서는 자동발송 됩니다)</small></span>
                                </label>
                            </div>

                            {/* Privacy Agreement */}
                            <div className="terms-agreement" style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f9f9f9', borderRadius: '6px', textAlign: 'left' }}>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.85rem' }}>
                                        <input
                                            type="checkbox"
                                            checked={privacyAgreed}
                                            onChange={(e) => setPrivacyAgreed(e.target.checked)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        <span>개인정보 수집 및 이용 동의 <strong style={{ color: '#e65100' }}>[필수]</strong></span>
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() => setShowPrivacy(!showPrivacy)}
                                        style={{
                                            background: 'none',
                                            border: 'none',
                                            color: '#666',
                                            textDecoration: 'underline',
                                            fontSize: '0.75rem',
                                            cursor: 'pointer',
                                            padding: '0'
                                        }}
                                    >
                                        {showPrivacy ? '접기' : '상세보기'}
                                    </button>
                                </div>

                                {/* Inline Privacy Details */}
                                {showPrivacy && (
                                    <div style={{
                                        marginTop: '8px',
                                        padding: '8px',
                                        backgroundColor: '#fff',
                                        border: '1px solid #eee',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        color: '#555',
                                        lineHeight: '1.4'
                                    }}>
                                        <p style={{ margin: '0 0 3px 0' }}><strong>1. 수집항목:</strong> 이메일, 연락처, 이름, 회사명</p>
                                        <p style={{ margin: '0 0 3px 0' }}><strong>2. 수집목적:</strong> 예상 견적서 발송 및 상담</p>
                                        <p style={{ margin: '0 0 0 0' }}><strong>3. 보유기간:</strong> 3년 (상담 이력 관리)</p>
                                    </div>
                                )}
                            </div>

                            <div style={{ display: 'flex', gap: '10px', marginTop: '15px' }}>
                                <button
                                    type="button"
                                    onClick={() => setShowResendModal(false)}
                                    style={{
                                        flex: 1,
                                        padding: '12px',
                                        borderRadius: '6px',
                                        border: '1px solid #ddd',
                                        background: '#fff',
                                        cursor: 'pointer'
                                    }}
                                >
                                    취소
                                </button>
                                <button
                                    type="button"
                                    onClick={handleSubmit}
                                    className="ae-btn primary"
                                    disabled={loading}
                                    style={{ flex: 1 }}
                                >
                                    {loading ? '발송 중...' : '견적서 발송'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
};

export default EstimateResult;
